import { Alert, Anchor, Button, Loader, Paper, Text, Title } from '@mantine/core';
import type { TRPCClientErrorLike } from '@trpc/client';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { AppRouter } from '~/server/api/root';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { api } from '~/trpc-extra/api';

const SUPPORT_EMAIL = 'support@stably.ai';
const SUPPORT_LINK_THRESHOLD = 3;

export function shouldBootstrapAuthGate({
  isLoggedIn,
  isLoading,
  orgCount,
}: {
  isLoggedIn: boolean | undefined;
  isLoading: boolean | undefined;
  orgCount: number;
}) {
  return Boolean(isLoggedIn) && !Boolean(isLoading) && orgCount === 0;
}

export function shouldShowBootstrapSupportLink(failureCount: number) {
  return failureCount >= SUPPORT_LINK_THRESHOLD;
}

function getBootstrapErrorMessage(error: TRPCClientErrorLike<AppRouter> | null) {
  return error?.message || 'We could not finish setting up your account.';
}

export function getBootstrapProjectRedirectPath(projectId: string | null | undefined) {
  return projectId ? `/project/${projectId}/home` : null;
}

export function AuthenticatedBootstrapGate({ children }: { children: ReactNode }) {
  const router = useRouter();
  const { isLoggedIn, loading, orgHelper, refreshAuthInfo } = useAuthData();
  const orgs = orgHelper?.getOrgs() ?? [];
  const { mutateAsync: bootstrapMutateAsync, isPending, error } = api.team.bootstrapSelfServe.useMutation();
  const [failureCount, setFailureCount] = useState(0);
  const autoBootstrapStartedRef = useRef(false);
  const hasEverBootstrappedRef = useRef(false);

  const shouldBootstrap = shouldBootstrapAuthGate({
    isLoggedIn,
    isLoading: loading,
    orgCount: orgs.length,
  });

  const attemptBootstrap = useCallback(async () => {
    try {
      const result = await bootstrapMutateAsync(undefined);
      setFailureCount(0);
      await refreshAuthInfo();

      // The bootstrap mutation already creates the first project. Route directly
      // to it so first-run onboarding does not depend on auth propagation timing
      // or a follow-up default-project lookup to escape the no-project state.
      const redirectPath = getBootstrapProjectRedirectPath(result.projectId);
      if (redirectPath) {
        await router.replace(redirectPath);
      }
    } catch {
      setFailureCount((current) => current + 1);
    }
  }, [bootstrapMutateAsync, refreshAuthInfo, router]);

  useEffect(() => {
    if (!shouldBootstrap) {
      autoBootstrapStartedRef.current = false;
      setFailureCount(0);
      return;
    }

    if (hasEverBootstrappedRef.current || autoBootstrapStartedRef.current) {
      return;
    }

    autoBootstrapStartedRef.current = true;
    hasEverBootstrappedRef.current = true;
    void attemptBootstrap();
  }, [shouldBootstrap, attemptBootstrap]);

  if (!shouldBootstrap) {
    return children;
  }

  return (
    <AccountSetupPendingScreen
      errorMessage={error ? getBootstrapErrorMessage(error) : undefined}
      isLoading={isPending}
      onRetry={() => {
        void attemptBootstrap();
      }}
      showSupportLink={shouldShowBootstrapSupportLink(failureCount)}
    />
  );
}

export function AccountSetupPendingScreen({
  errorMessage,
  isLoading,
  onRetry,
  showSupportLink,
}: {
  errorMessage?: string;
  isLoading: boolean;
  onRetry: () => void;
  showSupportLink: boolean;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] px-6 py-12 dark:bg-[linear-gradient(180deg,#111827_0%,#0f172a_100%)]">
      <Paper
        radius="lg"
        shadow="xl"
        className="w-full max-w-xl border border-slate-200/80 p-8 dark:border-slate-700/80"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            {isLoading && <Loader size="sm" />}
            <Text fw={600}>{isLoading ? 'Setting up your account...' : 'Account setup'}</Text>
          </div>

          <div>
            <Title order={2}>Creating your workspace</Title>
            <Text
              c="dimmed"
              mt="xs"
            >
              We&apos;re creating your first team and project so you can continue into billing without an admin step.
            </Text>
          </div>

          {errorMessage ?
            <Alert
              color="red"
              title="Setup failed"
              variant="light"
            >
              {errorMessage}
            </Alert>
          : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              loading={isLoading}
              onClick={onRetry}
            >
              Retry setup
            </Button>
            {showSupportLink ?
              <Anchor href={`mailto:${SUPPORT_EMAIL}`}>Contact support</Anchor>
            : null}
          </div>
        </div>
      </Paper>
    </div>
  );
}
